/**
 * GpuScope — the per-behaviour resource scope + rich script-facing wrappers of `engine.gpu` v2.
 *
 * The DI seam ({@link GpuBacking}) stays FLAT and numeric-handle based so any host (editor,
 * player, a future Jint/.NET runtime) can implement it without object marshalling; the ergonomic
 * surface scripts see ({@link ComputeKernel}/{@link GpuBuffer}/{@link GpuTexture}) is pure TS in
 * core, shared by every runtime, and routes everything through a {@link GpuScope} that:
 *
 *  - TRACKS every created handle per behaviour — `IEngineContext.dispose()` (fired by ScriptHost
 *    on script destroy / play stop / hot reload) frees exactly that script's resources;
 *  - CONTAINS failures, the async analog of `ScriptHost.safe`: async ops never reject (a failed
 *    `loadKernel` resolves a DEAD kernel with `ok:false` + `error`; a failed `read` resolves
 *    empty), post-dispose resolutions destroy the just-created resource and resolve dead, and
 *    every failure is logged through the script log channel instead of thrown.
 *
 * Design: .refactor/COMPUTE-SHADERS-DESIGN.md (§engine.gpu v2).
 */

import type { LogCaps } from "../IEngineContext";

// ============================================================================
// The flat DI seam a runtime implements (editor: SlangCompiler+cache; player: precompiled map)
// ============================================================================

/** Element types for script-created standalone buffers (array stride follows WGSL rules). */
export type GpuElementType = "float" | "float2" | "float3" | "float4" | "int" | "uint";

/** Formats accepted by {@link GpuScriptApi.createTexture} (browser-safe storage set + sampled). */
export type GpuTextureFormat =
  | "rgba8unorm"
  | "rgba16float"
  | "rgba32float"
  | "rg32float"
  | "r32float"
  | "r32uint"
  | "r32sint";

/** One bindable resource of a kernel (structurally compatible with render2d's ComputeBindingEntry). */
export interface GpuKernelResourceInfo {
  /** Script-facing bind name (struct-kernel field / module-level parameter). */
  name: string;
  kind: "buffer" | "rwbuffer" | "texture" | "rwtexture" | "uniform";
  /** Element/value type label, e.g. 'Particle', 'float4'. */
  type: string;
  /** Bytes: buffer element stride / uniform value size / 0 for textures. */
  size: number;
  /** Struct layout of a buffer's element type (struct elements only). */
  layout?: {
    name: string;
    stride: number;
    fields: { name: string; type: string; offset: number; size: number }[];
  };
}

/** Result of {@link GpuBacking.loadKernel} — dead on failure (`ok:false`), never a rejection. */
export interface GpuKernelHandle {
  /** Backing handle; -1 when dead. */
  id: number;
  ok: boolean;
  name: string;
  numThreads: [number, number, number];
  resources: GpuKernelResourceInfo[];
  /** Formatted diagnostics when dead. */
  error?: string;
}

/**
 * The injected GPU backing (DI per runtime). Buffers/textures/kernels are opaque numeric ids owned
 * by the backing; binding is by reflected parameter NAME (never positional). Implementations must
 * not throw from any method and must never reject `loadKernel`/`readBuffer` — failures surface as
 * dead handles / null results (crash containment is a contract, not a courtesy).
 */
export interface GpuBacking {
  /** True when a compute-capable device is available (WebGPU up, device not lost). */
  isAvailable(): boolean;
  /**
   * Resolve a compute-shader ASSET (guid) + kernel name (optional when the module declares exactly
   * one) into a dispatchable kernel. Editor compiles/loads from the import cache; player reads the
   * build-emitted precompiled map. NEVER rejects.
   */
  loadKernel(shaderRef: string, kernelName?: string): Promise<GpuKernelHandle>;
  destroyKernel(kernel: number): void;
  /** Create a storage buffer (storage | copy-dst | copy-src). Returns handle, -1 on failure. */
  createBuffer(byteLength: number, label?: string): number;
  writeBuffer(buffer: number, data: ArrayBufferView, byteOffset?: number): void;
  /** Read bytes back to the CPU (staging copy + map). Resolves null on any failure. */
  readBuffer(buffer: number, byteLength?: number, byteOffset?: number): Promise<ArrayBuffer | null>;
  destroyBuffer(buffer: number): void;
  /** Create a 2D texture usable as sampled + storage. Returns handle, -1 on failure. */
  createTexture(width: number, height: number, format?: string, label?: string): number;
  destroyTexture(texture: number): void;
  /** Bind a buffer/texture to a kernel's reflected parameter by name. Unknown name → warn once. */
  setBuffer(kernel: number, param: string, buffer: number): void;
  setTexture(kernel: number, param: string, texture: number): void;
  /** Set a named uniform (scalar or flat vector components). */
  setUniform(kernel: number, param: string, value: number | ArrayLike<number>): void;
  /** Dispatch raw workgroup counts. False (+ warn once) when bindings are incomplete/dead. */
  dispatch(kernel: number, x: number, y?: number, z?: number): boolean;
}

// ============================================================================
// Scope
// ============================================================================

/** WGSL array stride per {@link GpuElementType} (float3 pads to 16). */
const ELEMENT_STRIDE: Record<GpuElementType, number> = {
  float: 4, float2: 8, float3: 16, float4: 16, int: 4, uint: 4,
};
const ELEMENT_COMPONENTS: Record<GpuElementType, number> = {
  float: 1, float2: 2, float3: 3, float4: 4, int: 1, uint: 1,
};

type Scalar = "f32" | "i32" | "u32";
const scalarOf = (type: GpuElementType | "struct"): Scalar =>
  type === "int" ? "i32" : type === "uint" ? "u32" : "f32";
const typedArrayOf = (scalar: Scalar, buf: ArrayBuffer): Float32Array | Int32Array | Uint32Array =>
  scalar === "i32" ? new Int32Array(buf) : scalar === "u32" ? new Uint32Array(buf) : new Float32Array(buf);

/**
 * Tracks every GPU resource a single behaviour creates and frees them on script destroy. All
 * wrapper classes route through it; after {@link dispose} every op is a warn-once no-op.
 */
export class GpuScope {
  private readonly _buffers = new Set<number>();
  private readonly _textures = new Set<number>();
  private readonly _kernels = new Set<number>();
  private _disposed = false;
  private _warned = false;

  constructor(
    readonly backing: GpuBacking,
    /** Owner label for diagnostics (GameObject name). */
    readonly owner: string,
    private readonly _log?: LogCaps,
  ) {}

  get disposed(): boolean {
    return this._disposed;
  }

  warn(message: string): void {
    (this._log ?? console).warn(`[gpu] ${this.owner}: ${message}`);
  }

  /** One warn per scope for use-after-dispose (a destroyed entity spamming logs helps nobody). */
  guard(): boolean {
    if (!this._disposed) return true;
    if (!this._warned) {
      this._warned = true;
      this.warn("GPU call after the script was destroyed — ignored");
    }
    return false;
  }

  trackBuffer(id: number): void {
    if (id >= 0) this._buffers.add(id);
  }
  trackTexture(id: number): void {
    if (id >= 0) this._textures.add(id);
  }
  trackKernel(id: number): void {
    if (id >= 0) this._kernels.add(id);
  }
  untrackBuffer(id: number): void {
    this._buffers.delete(id);
  }
  untrackTexture(id: number): void {
    this._textures.delete(id);
  }
  untrackKernel(id: number): void {
    this._kernels.delete(id);
  }

  /** Free every tracked resource (idempotent). Fired via `IEngineContext.dispose()`. */
  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    for (const id of this._kernels) this.backing.destroyKernel(id);
    for (const id of this._buffers) this.backing.destroyBuffer(id);
    for (const id of this._textures) this.backing.destroyTexture(id);
    this._kernels.clear();
    this._buffers.clear();
    this._textures.clear();
  }
}

// ============================================================================
// Script-facing wrappers
// ============================================================================

/** A storage buffer owned by the creating script's scope (auto-freed on script destroy). */
export class GpuBuffer {
  /** @internal */
  constructor(
    private readonly _scope: GpuScope,
    /** @internal Backing handle (used by ComputeKernel.setBuffer). */
    readonly id: number,
    readonly type: GpuElementType | "struct",
    /** Element count. */
    readonly count: number,
    /** Element stride in bytes (WGSL array stride — float3 pads to 16). */
    readonly stride: number,
    private readonly _components: number,
  ) {}

  get byteLength(): number {
    return this.count * this.stride;
  }
  get ok(): boolean {
    return this.id >= 0 && !this._scope.disposed;
  }

  /**
   * Upload values. `ArrayLike<number>` is interpreted as TIGHT per-element components and strided
   * to the WGSL layout when needed (float3 → 16-byte elements); pass an `ArrayBufferView` to
   * upload raw pre-laid-out bytes (e.g. mixed-type struct elements built with a DataView).
   */
  write(data: ArrayLike<number> | ArrayBufferView, offsetElements = 0): void {
    if (!this._scope.guard() || this.id < 0) return;
    const byteOffset = offsetElements * this.stride;
    if (ArrayBuffer.isView(data)) {
      this._scope.backing.writeBuffer(this.id, data, byteOffset);
      return;
    }
    const scalar = scalarOf(this.type);
    const tightBytes = this._components * 4;
    if (tightBytes === this.stride || this.type === "struct") {
      // Tight layout (or struct data authored flat) — one typed-array upload.
      const view = scalar === "i32" ? Int32Array.from(data) : scalar === "u32" ? Uint32Array.from(data) : Float32Array.from(data);
      this._scope.backing.writeBuffer(this.id, view, byteOffset);
      return;
    }
    // Padded elements (float3): re-stride into the WGSL layout.
    const elements = Math.ceil(data.length / this._components);
    const strided = new ArrayBuffer(elements * this.stride);
    const dst = typedArrayOf(scalar, strided);
    const perElement = this.stride / 4;
    for (let e = 0; e < elements; e++) {
      for (let c = 0; c < this._components; c++) {
        const v = data[e * this._components + c];
        dst[e * perElement + c] = v ?? 0;
      }
    }
    this._scope.backing.writeBuffer(this.id, dst, byteOffset);
  }

  /**
   * Read values back (async staging copy). Resolves a typed array of TIGHT per-element components
   * (padding stripped); resolves EMPTY on failure or after dispose — never rejects.
   */
  async read(countElements?: number, offsetElements = 0): Promise<Float32Array | Int32Array | Uint32Array> {
    const scalar = scalarOf(this.type);
    const empty = () => typedArrayOf(scalar, new ArrayBuffer(0));
    if (!this._scope.guard() || this.id < 0) return empty();
    const n = countElements ?? this.count - offsetElements;
    const raw = await this._scope.backing.readBuffer(this.id, n * this.stride, offsetElements * this.stride);
    if (!raw || this._scope.disposed) return empty();
    const src = typedArrayOf(scalar, raw);
    const tight = this._components * 4;
    if (tight === this.stride || this.type === "struct") return src;
    // Strip per-element padding (float3 → xyz).
    const out = typedArrayOf(scalar, new ArrayBuffer(n * tight));
    const perElement = this.stride / 4;
    for (let e = 0; e < n; e++) {
      for (let c = 0; c < this._components; c++) out[e * this._components + c] = src[e * perElement + c];
    }
    return out;
  }

  /** Free now instead of at script destroy (optional — the scope frees everything anyway). */
  dispose(): void {
    if (this.id < 0 || this._scope.disposed) return;
    this._scope.backing.destroyBuffer(this.id);
    this._scope.untrackBuffer(this.id);
  }
}

/** A 2D texture owned by the creating script's scope (sampled + storage usage). */
export class GpuTexture {
  /** @internal */
  constructor(
    private readonly _scope: GpuScope,
    /** @internal Backing handle (used by ComputeKernel.setTexture). */
    readonly id: number,
    readonly width: number,
    readonly height: number,
    readonly format: GpuTextureFormat,
  ) {}

  get ok(): boolean {
    return this.id >= 0 && !this._scope.disposed;
  }

  dispose(): void {
    if (this.id < 0 || this._scope.disposed) return;
    this._scope.backing.destroyTexture(this.id);
    this._scope.untrackTexture(this.id);
  }
}

/**
 * A loaded, dispatchable compute kernel. Obtained via `engine.gpu.loadKernel(assetGuid, name?)`;
 * NEVER null — a failed load yields a DEAD kernel (`ok === false`, `error` set) whose methods are
 * warn-once no-ops, so scripts stay linear without try/catch pyramids.
 */
export class ComputeKernel {
  private _deadWarned = false;

  /** @internal */
  constructor(
    private readonly _scope: GpuScope,
    private readonly _handle: GpuKernelHandle,
  ) {}

  get ok(): boolean {
    return this._handle.ok && this._handle.id >= 0 && !this._scope.disposed;
  }
  get name(): string {
    return this._handle.name;
  }
  /** Formatted compile/load diagnostics when the kernel is dead. */
  get error(): string | undefined {
    return this._handle.error;
  }
  get numThreads(): readonly [number, number, number] {
    return this._handle.numThreads;
  }
  /** The kernel's reflected bindable resources (buffers/textures/uniforms, by name). */
  get resources(): readonly GpuKernelResourceInfo[] {
    return this._handle.resources;
  }

  /** Reflection lookup for one resource, or undefined. */
  resource(name: string): GpuKernelResourceInfo | undefined {
    return this._handle.resources.find((r) => r.name === name);
  }

  private guard(): boolean {
    if (!this._scope.guard()) return false;
    if (this._handle.ok && this._handle.id >= 0) return true;
    if (!this._deadWarned) {
      this._deadWarned = true;
      this._scope.warn(`kernel '${this._handle.name}' is dead${this._handle.error ? ` — ${this._handle.error}` : ""}`);
    }
    return false;
  }

  /** Bind a buffer to a reflected (rw)buffer parameter by name. Chainable. */
  setBuffer(name: string, buffer: GpuBuffer): this {
    if (this.guard()) this._scope.backing.setBuffer(this._handle.id, name, buffer.id);
    return this;
  }

  /** Bind a texture to a reflected (rw)texture parameter by name. Chainable. */
  setTexture(name: string, texture: GpuTexture): this {
    if (this.guard()) this._scope.backing.setTexture(this._handle.id, name, texture.id);
    return this;
  }

  /** Set a named uniform (scalar or flat components, e.g. `[x, y]` for float2). Chainable. */
  setUniform(name: string, value: number | ArrayLike<number>): this {
    if (this.guard()) this._scope.backing.setUniform(this._handle.id, name, value);
    return this;
  }

  /** Set several uniforms at once. Chainable. */
  setUniforms(values: Record<string, number | ArrayLike<number>>): this {
    for (const [name, value] of Object.entries(values)) this.setUniform(name, value);
    return this;
  }

  /**
   * Reflection-aware convenience: create a buffer sized `count × <element stride>` from the named
   * resource's reflected layout AND bind it. The one-liner for the common case:
   * `const buf = kernel.createBuffer('particles', 100_000);`
   */
  createBuffer(resourceName: string, count: number, data?: ArrayLike<number> | ArrayBufferView): GpuBuffer {
    const res = this.resource(resourceName);
    const dead = new GpuBuffer(this._scope, -1, "struct", 0, 4, 1);
    if (!this.guard()) return dead;
    if (!res || (res.kind !== "buffer" && res.kind !== "rwbuffer")) {
      this._scope.warn(
        `kernel '${this.name}': no buffer resource '${resourceName}' (have: ${this._handle.resources.filter((r) => r.kind.endsWith("buffer")).map((r) => r.name).join(", ") || "none"})`,
      );
      return dead;
    }
    const stride = Math.max(4, res.size);
    const id = this._scope.backing.createBuffer(count * stride, `${this.name}.${resourceName}`);
    this._scope.trackBuffer(id);
    // Scalar element types round-trip tightly; structs read/write raw (or via ArrayBufferView).
    const elementType = (["float", "float2", "float3", "float4", "int", "uint"] as const).find((t) => t === res.type) ?? "struct";
    const components = elementType === "struct" ? stride / 4 : ELEMENT_COMPONENTS[elementType];
    const buffer = new GpuBuffer(this._scope, id, elementType, count, stride, components);
    if (data !== undefined && id >= 0) buffer.write(data);
    this.setBuffer(resourceName, buffer);
    return buffer;
  }

  /** Dispatch RAW workgroup counts. False when dead/unbound (warn-once, never throws). */
  dispatch(x: number, y = 1, z = 1): boolean {
    if (!this.guard()) return false;
    return this._scope.backing.dispatch(this._handle.id, Math.max(1, Math.ceil(x)), Math.max(1, Math.ceil(y)), Math.max(1, Math.ceil(z)));
  }

  /**
   * Dispatch by ELEMENT counts — workgroups are `ceil(n / numThreads)` per dimension, so
   * `kernel.dispatchElements(N)` runs at least N invocations of a `[NumThreads(64)]` kernel.
   */
  dispatchElements(x: number, y = 1, z = 1): boolean {
    const [tx, ty, tz] = this._handle.numThreads;
    return this.dispatch(Math.ceil(x / tx), Math.ceil(y / ty), Math.ceil(z / tz));
  }

  /** Free now instead of at script destroy (optional). */
  dispose(): void {
    if (this._handle.id < 0 || this._scope.disposed) return;
    this._scope.backing.destroyKernel(this._handle.id);
    this._scope.untrackKernel(this._handle.id);
    this._handle.ok = false;
  }
}

// ============================================================================
// Scope-level factory helpers (used by GpuScriptApi; kept here so wrappers stay package-private)
// ============================================================================

/** @internal Create a dead kernel wrapper (failed load). */
export function deadKernel(scope: GpuScope, name: string, error: string): ComputeKernel {
  return new ComputeKernel(scope, { id: -1, ok: false, name, numThreads: [1, 1, 1], resources: [], error });
}

/** @internal Standalone buffer creation (element type known, no kernel reflection needed). */
export function createScopedBuffer(
  scope: GpuScope,
  type: GpuElementType,
  count: number,
  data?: ArrayLike<number> | ArrayBufferView,
  label?: string,
): GpuBuffer {
  if (!scope.guard()) return new GpuBuffer(scope, -1, type, 0, ELEMENT_STRIDE[type], ELEMENT_COMPONENTS[type]);
  const stride = ELEMENT_STRIDE[type];
  const id = scope.backing.createBuffer(count * stride, label);
  scope.trackBuffer(id);
  const buffer = new GpuBuffer(scope, id, type, count, stride, ELEMENT_COMPONENTS[type]);
  if (data !== undefined && id >= 0) buffer.write(data);
  return buffer;
}

/** @internal Standalone texture creation. */
export function createScopedTexture(
  scope: GpuScope,
  width: number,
  height: number,
  format: GpuTextureFormat = "rgba32float",
  label?: string,
): GpuTexture {
  if (!scope.guard()) return new GpuTexture(scope, -1, width, height, format);
  const id = scope.backing.createTexture(width, height, format, label);
  scope.trackTexture(id);
  return new GpuTexture(scope, id, width, height, format);
}

/** @internal Kernel load with full containment (never rejects; post-dispose loads are destroyed). */
export async function loadScopedKernel(
  scope: GpuScope,
  shaderRef: string,
  kernelName?: string,
): Promise<ComputeKernel> {
  const label = kernelName ?? shaderRef;
  if (scope.disposed) return deadKernel(scope, label, "script destroyed");
  try {
    const handle = await scope.backing.loadKernel(shaderRef, kernelName);
    if (scope.disposed) {
      // Resolved after the script died: free immediately, hand back a dead wrapper.
      if (handle.ok && handle.id >= 0) scope.backing.destroyKernel(handle.id);
      return deadKernel(scope, handle.name || label, "script destroyed");
    }
    if (!handle.ok) {
      scope.warn(`loadKernel('${shaderRef}'${kernelName ? `, '${kernelName}'` : ""}) failed — ${handle.error ?? "unknown error"}`);
      return new ComputeKernel(scope, handle);
    }
    scope.trackKernel(handle.id);
    return new ComputeKernel(scope, handle);
  } catch (err) {
    // Backings must not reject — but if one does, containment still holds.
    const message = err instanceof Error ? err.message : String(err);
    scope.warn(`loadKernel('${shaderRef}') rejected — ${message}`);
    return deadKernel(scope, label, message);
  }
}
